import useLiveOrders from "@/Pages/Auth/Bestellingen/Live/useLiveOrders";
import useAccountQuery from "@/shared/hooks/useAccountQuery";
import useElectronHook from "@/shared/hooks/useElectronHook";
import useLiveOrdersHook from "@/shared/hooks/useLiveOrdersHook";
import useOrders from "@/shared/hooks/useOrders";
import usePrintReceipt from "@/shared/hooks/usePrintReceipt";
import useSoundHook from "@/shared/hooks/useSoundHook";
import useUserSettingsHook from "@/shared/hooks/useUserSettingsHook";
import { OrderStatus, PaymentStatusEnum, Sources } from "@/shared/utils/enum";
import { asArray, getValue } from "@/shared/utils/helpers";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import { useEffect, useMemo, useState } from "react";

const playedOrderIds = [];
let printedOrderIds = [];

const LayoutLive = () => {
  const { query } = useUserSettingsHook();
  const { data, refetch } = useOrders();
  const liveOrders = useLiveOrders(data);
  const { updateStatusToConfirmed } = useLiveOrdersHook();
  const printReceipt = usePrintReceipt();
  const { isElectron } = useElectronHook();
  const [playSound, setPlaySound] = useState(false);
  useSoundHook(liveOrders, playSound, setPlaySound);
  const { data: accountData } = useAccountQuery();

  const {
    is_order_accepted_automatically,
    is_pos_order_accepted_automatically = query?.data
      ?.is_pos_order_accepted_automatically ?? true,
    is_pos_order_printed_automatically = query?.data
      ?.is_pos_order_printed_automatically ?? true,
  } = query?.data || {};

  const hasArrivedOrders = useMemo(() => {
    return asArray(liveOrders?.[0])?.filter((order) => {
      const value = order?.order_information?.order_status;

      const userPrintedDate =
        order?.order_information?.receipt_printer_printed_datetime?.[
          accountData?.item_id
        ];

      const paymentStatus = order?.order_information?.order_payment_status;
      return (
        (value === OrderStatus.ARRIVED || (isElectron && !userPrintedDate)) &&
        paymentStatus !== PaymentStatusEnum.PENDING_PAYMENT
      );
    });
  }, [liveOrders]);

  useEffect(() => {
    if (isElectron && hasArrivedOrders?.length > 0) {
      window.api.send("newOrder", null);
    }
    const hasUnplayedOrder = hasArrivedOrders?.some(
      (o) => playedOrderIds.includes(o.order_information.item_id) === false
    );
    setPlaySound(hasUnplayedOrder ? true : false);
    hasArrivedOrders?.forEach((o) =>
      playedOrderIds.push(o.order_information.item_id)
    );
  }, [hasArrivedOrders]);

  const arrivedOrders = useMemo(() => {
    return hasArrivedOrders?.length > 0
      ? hasArrivedOrders.filter((order: OrderV2) =>
          order?.order_information?.is_custom_time_slot_selected ||
          (is_order_accepted_automatically &&
            is_pos_order_accepted_automatically)
            ? true
            : is_order_accepted_automatically &&
              !is_pos_order_accepted_automatically
            ? order?.order_information?.source !== 19
            : !is_order_accepted_automatically &&
              is_pos_order_accepted_automatically
            ? order?.order_information?.source === 19
            : false
        )
      : [];
  }, [hasArrivedOrders]);

  useEffect(() => {
    if (arrivedOrders?.length > 0) {
      arrivedOrders?.forEach((order) => {
        const orderId = getValue(order?.order_information?.item_id);
        const status = order?.order_information?.order_status;
        const branchId = order?.order_information?.branch_id;
        if (status === OrderStatus.ARRIVED) {
          updateStatusToConfirmed
            .mutateAsync({ orderId, branchId })
            .then(() => printOrder());
        } else {
          printOrder();
        }
      });
    }
  }, [arrivedOrders]);

  const printOrder = () => {
    if (
      isElectron &&
      (is_order_accepted_automatically || is_pos_order_accepted_automatically)
    ) {
      arrivedOrders?.forEach(async (order) => {
        const isKassaOrder = order.order_information?.source === Sources.POS;
        const orderId = order.order_information.item_id;
        const retryCount =
          order?.order_information?.receipt_print_retry_count ?? 3;

        if (
          !printedOrderIds.includes(orderId) &&
          !(isKassaOrder && is_pos_order_printed_automatically === false) &&
          retryCount > 0
        ) {
          printedOrderIds.push(orderId);
          await printReceipt(orderId, order.order_information.branch_id).catch(
            (error) => {
              printedOrderIds = [];
              if (error?.response.status === 406) {
                refetch();
              }
            }
          );
        }
      });
    }
  };

  return <></>;
};

export default LayoutLive;
